import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  BrowserEventService,
  ColorScheme,
  LocaleSetting,
  supportedLocalesKey
} from '@rct/lynx-lobbies-shared-lib';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AvailableLocaleOptions, LocaleOption } from '../../../../constants/locale-options.constant';
import { LynxAuthService } from '../../../../services/auth.service';
import { LynxLobbyService } from '../../../../services/lobby.service';
import { LynxLocaleSettingService } from '../../../../services/locale-setting.service';

@Component({
    selector: 'lynx-locale-selector',
    templateUrl: './locale-selector.component.html',
    styleUrls: ['./locale-selector.component.scss'],
    standalone: false
})
export class LocaleSelectorComponent implements OnInit, OnDestroy {
  @Input() colorScheme: ColorScheme = ColorScheme.Light;
  @Input() dropDownPlacement: 'left' | 'right' = 'right';

  ColorScheme = ColorScheme;

  isMenuDropDownOpen: boolean = false;
  isMobile: boolean = this._browserEventService.isMobile();
  isLoggedIn: boolean = false;
  localeOptions: LocaleOption[] = [];
  selectedLocaleOption: LocaleOption;
  currentUrl: string = '';

  private _availableLocaleOptions: LocaleOption[] = AvailableLocaleOptions;
  private _destroy$ = new Subject<boolean>();

  constructor(
    private _router: Router,
    private _elementRef: ElementRef,
    private _browserEventService: BrowserEventService,
    private _lobbyService: LynxLobbyService,
    private _authService: LynxAuthService,
    private _localeSettingService: LynxLocaleSettingService
  ) {}

  ngOnInit(): void {
    this._router.events.pipe(takeUntil(this._destroy$)).subscribe((events) => {
      if (events instanceof NavigationEnd) {
        const { urlAfterRedirects } = events;
        this.currentUrl = urlAfterRedirects;
      }
    });

    this._authService.isLoggedIn$
      .pipe(takeUntil(this._destroy$))
      .subscribe((isLoggedIn: boolean | undefined) => {
        this.isLoggedIn = !!isLoggedIn;
      });

    this._localeSettingService
      .getLocaleSetting$()
      .pipe(takeUntil(this._destroy$))
      .subscribe((localeSetting: LocaleSetting) => {
        if (
          !!localeSetting &&
          localeSetting[supportedLocalesKey] &&
          Array.isArray(localeSetting[supportedLocalesKey])
        ) {
          this.localeOptions = this._availableLocaleOptions.filter(
            (localeOption) =>
              localeSetting[supportedLocalesKey].includes(localeOption.code)
          );

          this.selectedLocaleOption = this._availableLocaleOptions.find(
            (localeOption) =>
              localeOption.code ===
              this._lobbyService.getSiteLocale(localeSetting)
          );
        }
      });
  }

  onSelectLocale(localeOption: LocaleOption): void {
    this.selectedLocaleOption = localeOption;

    this._lobbyService
      .onSelectLocale$(localeOption.code, this.currentUrl, this.isLoggedIn)
      .pipe(take(1))
      .subscribe(() => (this.isMenuDropDownOpen = false));
  }

  toggleMenuDropDown(): void {
    this.isMenuDropDownOpen = !this.isMenuDropDownOpen;
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutsideOfMenuDropDown(targetElement: any): void {
    const isClickedInside =
      this._elementRef.nativeElement.contains(targetElement);

    if (!isClickedInside) {
      this.isMenuDropDownOpen = false;
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }
}
